import React, { useEffect, useState } from 'react';
import { API_BASE_URL, authHeaders } from '../../config';
import {
  IonPage,
  IonContent,
  IonBadge,
  IonIcon,
  IonSearchbar,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonModal,
  RefresherEventDetail
} from '@ionic/react';
import {
  personOutline, callOutline, locationOutline,
  businessOutline, eyeOutline, pencilOutline, closeOutline,
  calendarOutline, maleFemaleOutline, trailSignOutline, downloadOutline,
  peopleOutline
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { PageHeader, AppBackground } from '../../components/PageHeader';
import { AlertAceptar } from '../../components/alerts/AlertAceptar';
import { useUser } from '../../context/UserContext';
import './verReferidos.css';
import * as XLSX from 'xlsx';

// ── Tipos ────────────────────────────────────────────────────────────────────

interface ReferidoHijo {
  id: number;
  nombre_completo: string;
  numero_cedula: string;
  telefono: string;
  lugar_de_votacion: string;
  mesa_de_votacion: string;
  municipio_nombre?: string;
}

interface Referido {
  id: number;
  numero_cedula: string;
  nombre_completo: string;
  alias: string;
  edad: string;
  genero: string;
  direccion_de_residencia: string;
  municipio_id: number | null;
  municipio_nombre: string;
  departamento_nombre: string;
  lugar_de_votacion: string;
  mesa_de_votacion: string;
  telefono: string;
  presentador_id: number | null;
  presentador_nombre: string | null;
  usuario_id: number | null;
}

interface ReferidoDetalle extends Referido {
  referidos_hijos: ReferidoHijo[];
}

// Alias entre comillas y cédula, solo los que existan: "Pacho" · C.C. 123
const identificacion = (r: { alias?: string; numero_cedula?: string }) =>
  [r.alias && `“${r.alias}”`, r.numero_cedula && `C.C. ${r.numero_cedula}`].filter(Boolean).join(' · ');

// ── Componente ───────────────────────────────────────────────────────────────

const VerReferidos: React.FC = () => {
  const [referidos, setReferidos] = useState<Referido[]>([]);
  const [filteredReferidos, setFilteredReferidos] = useState<Referido[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedReferido, setSelectedReferido] = useState<ReferidoDetalle | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const history = useHistory();
  const { user } = useUser();
  const esAdmin = user?.rol === 'admin';
  const [exportando, setExportando] = useState(false);
  const [aviso, setAviso] = useState('');

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchReferidos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/referidos/todos`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      if (result.status === 'success') {
        setReferidos(result.data);
        setFilteredReferidos(result.data);
      }
    } catch (error) {
      console.error('Error fetching referidos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReferidos(); }, []);

  // ── Filtro de búsqueda ────────────────────────────────────────────────────

  useEffect(() => {
    if (searchText === '') {
      setFilteredReferidos(referidos);
    } else {
      const q = searchText.toLowerCase();
      // Cédula, alias y teléfono son opcionales: se buscan solo si existen
      setFilteredReferidos(referidos.filter(r =>
        [r.nombre_completo, r.alias, r.numero_cedula, r.telefono, r.municipio_nombre]
          .some(v => (v ?? '').toLowerCase().includes(q))
      ));
    }
  }, [searchText, referidos]);

  // ── Refresh ───────────────────────────────────────────────────────────────

  const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    fetchReferidos().then(() => event.detail.complete());
  };

  // ── Detalle modal ─────────────────────────────────────────────────────────

  const openDetailModal = async (ref: Referido) => {
    setLoadingDetalle(true);
    setShowModal(true);
    try {
      const response = await fetch(`${API_BASE_URL}/referidos/${ref.id}`, { headers: authHeaders() });
      const result = await response.json();
      if (result.status === 'success') {
        setSelectedReferido(result.data);
      }
    } catch (error) {
      console.error('Error fetching detalle referido:', error);
      // Mostrar datos básicos que ya tenemos
      setSelectedReferido({ ...ref, referidos_hijos: [] });
    } finally {
      setLoadingDetalle(false);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────

  // Solo administradores. Los datos salen de una ruta que el backend protege por rol,
  // así que ocultar el botón no es la única barrera.
  const exportToExcel = async () => {
    setExportando(true);
    let filas: Referido[];
    try {
      const response = await fetch(`${API_BASE_URL}/referidos/exportar`, { headers: authHeaders() });
      const result = await response.json();
      if (!response.ok || result.status !== 'success') {
        setAviso(result.message || result.error || 'No se pudo exportar.');
        return;
      }
      filas = result.data;
    } catch (error) {
      setAviso('No se pudo conectar con el servidor para exportar.');
      return;
    } finally {
      setExportando(false);
    }

    // Respeta la búsqueda activa: exporta solo lo que se está viendo
    const visibles = new Set(filteredReferidos.map(r => r.id));
    const data = filas.filter(item => searchText === '' || visibles.has(item.id)).map((item) => ({
      Nombre: item.nombre_completo,
      Alias: item.alias,
      Cedula: item.numero_cedula,
      Edad: item.edad,
      Genero: item.genero,
      Telefono: item.telefono,
      Direccion: item.direccion_de_residencia,
      Municipio: item.municipio_nombre,
      Departamento: item.departamento_nombre,
      Lugar_Votacion: item.lugar_de_votacion,
      Mesa_Votacion: item.mesa_de_votacion,
      Presentador: item.presentador_nombre || 'Directo',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Referidos');
    XLSX.writeFile(workbook, `referidos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <IonPage className="app-page ver-referidos-page">
      <AppBackground />

      <PageHeader
        title="Red de Referidos"
        subtitle={`${filteredReferidos.length} personas registradas`}
        backHref="/panel"
        actions={esAdmin && (
          <button className="export-btn" onClick={exportToExcel} disabled={exportando} title="Exportar a Excel">
            <IonIcon icon={downloadOutline} />
            <span>{exportando ? 'Exportando…' : 'Excel'}</span>
          </button>
        )}
      />

      <AlertAceptar isOpen={aviso !== ''} message={aviso} onDidDismiss={() => setAviso('')} />

      <div className="search-container">
        <IonSearchbar
          value={searchText}
          onIonInput={(e: any) => setSearchText(e.target.value)}
          placeholder="Buscar por nombre, alias, cédula..."
          className="custom-searchbar"
          animated
        />
      </div>

      <IonContent className="ion-padding-horizontal content-transparent" scrollEvents={true}>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        {loading ? (
          <div className="skeleton-list">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="skeleton-card">
                <IonSkeletonText animated style={{ width: '80%', height: '20px', borderRadius: '10px' }} />
                <IonSkeletonText animated style={{ width: '60%', height: '15px', borderRadius: '10px', marginTop: '10px' }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="referidos-list">
            {filteredReferidos.length > 0 ? (
              filteredReferidos.map((ref) => (
                <div key={ref.id} className="referido-card">
                  <div className="card-top">
                    <div className="avatar-small">
                      <IonIcon icon={personOutline} />
                    </div>
                    <div className="name-info">
                      <h4>{ref.nombre_completo}</h4>
                      <p>{identificacion(ref) || 'Sin cédula'}</p>
                    </div>
                    <IonBadge className="custom-badge">
                      {ref.genero || 'N/A'}
                    </IonBadge>
                  </div>

                  <div className="card-details">
                    <div className="detail-item">
                      <IonIcon icon={callOutline} />
                      <span>{ref.telefono || "Sin teléfono"}</span>
                    </div>
                    <div className="detail-item">
                      <IonIcon icon={locationOutline} />
                      <span>
                        {ref.municipio_nombre || 'Sin municipio'}
                        {ref.departamento_nombre ? `, ${ref.departamento_nombre}` : ''}
                      </span>
                    </div>
                    <div className="detail-item">
                      <IonIcon icon={businessOutline} />
                      <span>{ref.lugar_de_votacion || 'N/A'} — Mesa {ref.mesa_de_votacion || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="card-footer">
                    <span className="referido-por">
                      Referido por: <strong>{ref.presentador_nombre}</strong>
                    </span>
                    <div className="card-actions">
                      <button className="action-icon-btn view-btn" onClick={() => openDetailModal(ref)} title="Ver detalle">
                        <IonIcon icon={eyeOutline} />
                      </button>
                      <button className="action-icon-btn edit-btn" onClick={() => history.push(`/editar-referido/${ref.id}`)} title="Editar">
                        <IonIcon icon={pencilOutline} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <IonIcon icon={personOutline} />
                <h3>No se encontraron referidos</h3>
                <p>Intenta con otro término de búsqueda</p>
              </div>
            )}
          </div>
        )}

        {/* ── Modal de Detalle ── */}
        <IonModal
          isOpen={showModal}
          onDidDismiss={() => { setShowModal(false); setSelectedReferido(null); }}
          className="referido-detail-modal"
          breakpoints={[0, 0.5, 0.92]}
          initialBreakpoint={0.92}
        >
          <div className="modal-wrapper">
            <div className="modal-header">
              <div className="modal-handle" />
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <IonIcon icon={closeOutline} />
              </button>
            </div>

            {loadingDetalle ? (
              <div style={{ padding: '20px' }}>
                <IonSkeletonText animated style={{ width: '60%', height: '24px', borderRadius: '8px' }} />
                <IonSkeletonText animated style={{ width: '40%', height: '16px', borderRadius: '8px', marginTop: '8px' }} />
              </div>
            ) : selectedReferido && (
              <div className="modal-body">
                <div className="modal-profile">
                  <div className="modal-avatar">
                    <IonIcon icon={personOutline} />
                  </div>
                  <h3>{selectedReferido.nombre_completo}</h3>
                  <p>{identificacion(selectedReferido) || 'Sin cédula'}</p>
                </div>

                <div className="modal-info-grid">
                  <div className="modal-info-card full-width">
                    <div className="info-row">
                      <IonIcon icon={callOutline} className="green" />
                      <div>
                        <label>Teléfono</label>
                        <span>{selectedReferido.telefono || "Sin teléfono"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-section">
                  <h4>Ubicación y Votación</h4>
                  <div className="location-box">
                    <div className="loc-item">
                      <IonIcon icon={locationOutline} />
                      <div>
                        <label>Dirección</label>
                        <p>{selectedReferido.direccion_de_residencia || 'N/A'}</p>
                        <p className="city">
                          {selectedReferido.municipio_nombre || 'Sin municipio'}
                          {selectedReferido.departamento_nombre ? `, ${selectedReferido.departamento_nombre}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="loc-item">
                      <IonIcon icon={businessOutline} />
                      <div>
                        <label>Puesto de Votación</label>
                        <p>{selectedReferido.lugar_de_votacion || 'N/A'} — Mesa {selectedReferido.mesa_de_votacion || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Presentador */}
                <div>
                  <div className="referido-badge">
                    <IonIcon icon={trailSignOutline} />
                    <span>Referido por: <strong>{selectedReferido.presentador_nombre || 'Directo (sin presentador)'}</strong></span>
                  </div>
                </div>

                {/* Referidos Hijos */}
                <div className="modal-section" style={{ marginTop: '20px' }}>
                  <h4>
                    <IonIcon icon={peopleOutline} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Personas que presentó ({selectedReferido.referidos_hijos?.length || 0})
                  </h4>
                  <div className="modal-table-container">
                    {selectedReferido.referidos_hijos && selectedReferido.referidos_hijos.length > 0 ? (
                      <table className="modal-table">
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Cédula</th>
                            <th>Teléfono</th>
                            <th>Puesto / Mesa</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedReferido.referidos_hijos.map((hijo) => (
                            <tr key={hijo.id}>
                              <td><strong>{hijo.nombre_completo}</strong></td>
                              <td>{hijo.numero_cedula}</td>
                              <td>{hijo.telefono}</td>
                              <td>{hijo.lugar_de_votacion || 'N/A'} — Mesa {hijo.mesa_de_votacion || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="no-referidos-msg">
                        Este referido no ha presentado a nadie todavía.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default VerReferidos;
